import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const CONTACT_BG =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=85";

const Contact = () => {
  useSEO({
    title: "Contact",
    description: "Plan your next journey with Shaurya Travels. Call +91 9371212211 or send an enquiry — quick reply on WhatsApp.",
    path: "/contact",
  });
  const { settings } = useOutletContext();
  const [params] = useSearchParams();
  const initialDest = params.get("destination") || "";
  const initialVehicle = params.get("vehicle") || "";

  const [destinations, setDestinations] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    destination: initialDest,
    message: initialVehicle
      ? `I'd like to enquire about the ${initialVehicle}.`
      : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/destinations").then((r) => setDestinations(r.data || [])).catch(() => {});
  }, []);

  const destOptions = useMemo(() => {
    const names = (destinations || []).map((d) => d.name);
    // If initial destination is not present (e.g. came from a custom URL), include it
    if (initialDest && !names.includes(initialDest)) names.unshift(initialDest);
    return names;
  }, [destinations, initialDest]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Name and contact number are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        destination: form.destination,
        message: form.message,
        source: "contact-page",
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <img src={CONTACT_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-16 text-white">
          <p className="text-xs uppercase tracking-[0.4em] opacity-80 mb-4 fade-up">— Reach out</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] fade-up-2">
            Let's plan
            <br /><span className="italic opacity-80">your journey.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-16 pb-8">
        <div className="lg:col-span-5">
          <h3 className="font-serif-display text-2xl mb-8">Direct lines</h3>
          <ul className="space-y-6 text-[var(--st-ink-2)]">
            <li className="flex items-start gap-4">
              <Phone className="text-[var(--st-accent)] mt-1" size={18} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-60">Phone</div>
                <div className="text-base text-[var(--st-ink)]">{settings?.contact_phone || "—"}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail className="text-[var(--st-accent)] mt-1" size={18} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-60">Email</div>
                <div className="text-base text-[var(--st-ink)]">{settings?.contact_email || "—"}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <MapPin className="text-[var(--st-accent)] mt-1" size={18} />
              <div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-60">Studio</div>
                <div className="text-base text-[var(--st-ink)]">{settings?.contact_address || "—"}</div>
              </div>
            </li>
          </ul>
        </div>

        {sent ? (
          <div
            data-testid="contact-sent"
            className="lg:col-span-7 bg-[var(--st-surface)] border border-[var(--st-border)] p-12 text-center flex flex-col items-center justify-center"
          >
            <CheckCircle2 size={48} className="text-[var(--st-accent)] mb-6" />
            <h3 className="font-serif-display text-3xl mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="text-[var(--st-ink-2)] max-w-md mb-8">
              We have received your enquiry. Our team will reach out to you on{" "}
              <span className="text-[var(--st-ink)] font-medium">{form.phone}</span> shortly.
            </p>
            <button
              onClick={() => {
                setSent(false);
                setForm({ name: "", phone: "", email: "", destination: "", message: "" });
              }}
              data-testid="contact-send-another"
              className="px-6 py-3 text-xs uppercase tracking-[0.25em] border border-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-white transition"
            >
              Send another enquiry
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[var(--st-surface)] border border-[var(--st-border)] p-8 sm:p-12 space-y-6"
          >
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Name *</label>
              <input
                required
                data-testid="contact-name-input"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition"
                value={form.name}
                onChange={onChange("name")}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Contact number *</label>
              <input
                required
                type="tel"
                inputMode="tel"
                pattern="[0-9 +()-]{7,}"
                placeholder="+91 98765 43210"
                data-testid="contact-phone-input"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition"
                value={form.phone}
                onChange={onChange("phone")}
              />
              <p className="text-[10px] uppercase tracking-[0.25em] mt-1 opacity-60">
                We'll call/whatsapp you on this number.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">
                Destination to travel
              </label>
              <select
                data-testid="contact-destination-select"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition appearance-none"
                value={form.destination}
                onChange={onChange("destination")}
              >
                <option value="">— Choose a destination —</option>
                {destOptions.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="Other / Custom">Other / Custom</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Email (optional)</label>
              <input
                type="email"
                data-testid="contact-email-input"
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition"
                value={form.email}
                onChange={onChange("email")}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.25em] opacity-70">Tell us about your journey (optional)</label>
              <textarea
                data-testid="contact-message-input"
                rows={4}
                className="mt-2 w-full bg-transparent border-b border-[var(--st-border)] py-3 focus:outline-none focus:border-[var(--st-accent)] transition resize-none"
                value={form.message}
                onChange={onChange("message")}
              />
            </div>

            {error && (
              <div data-testid="contact-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
                {String(error)}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="btn-accent px-8 py-4 text-xs uppercase tracking-[0.25em] inline-flex items-center gap-3 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {submitting ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
};

export default Contact;
